import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Admin API
export const adminAPI = {
  login: async (email, password) => {
    const response = await apiClient.post('/admin/login', { email, password });
    return response.data;
  },
  getDashboardStats: async () => {
    const response = await apiClient.get('/admin/dashboard/stats');
    return response.data;
  },
  getUsers: async (status = null) => {
    const params = status ? { status } : {};
    const response = await apiClient.get('/admin/users', { params });
    return response.data;
  },
  approveUser: async (userId) => {
    const response = await apiClient.put(`/admin/users/${userId}/approve`);
    return response.data;
  },
  suspendUser: async (userId) => {
    const response = await apiClient.put(`/admin/users/${userId}/suspend`);
    return response.data;
  },
  terminateUser: async (userId) => {
    const response = await apiClient.put(`/admin/users/${userId}/terminate`);
    return response.data;
  },
  creditUser: async (userId, amount, description) => {
    const response = await apiClient.post(`/admin/users/${userId}/credit`, {
      userId,
      amount,
      description
    });
    return response.data;
  },
  debitUser: async (userId, amount, description) => {
    const response = await apiClient.post(`/admin/users/${userId}/debit`, {
      userId,
      amount,
      description
    });
    return response.data;
  },
  getTransactions: async () => {
    const response = await apiClient.get('/admin/transactions');
    return response.data;
  },
  getMessages: async () => {
    const response = await apiClient.get('/admin/messages');
    return response.data;
  },
  resetPassword: async (userId) => {
    const response = await apiClient.put(`/admin/users/${userId}/reset-password`);
    return response.data;
  },
  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.put('/admin/password', {
      currentPassword,
      newPassword
    });
    return response.data;
  },
  deleteTransaction: async (transactionId) => {
    const response = await apiClient.delete(`/admin/transactions/${transactionId}`);
    return response.data;
  },
  activateUser: async (userId) => {
    const response = await apiClient.put(`/admin/users/${userId}/activate`);
    return response.data;
  },
  deleteUser: async (userId) => {
    const response = await apiClient.delete(`/admin/users/${userId}`);
    return response.data;
  },
  suspendWireTransfer: async (userId) => {
    const response = await apiClient.put(`/admin/users/${userId}/suspend-wire-transfer`);
    return response.data;
  },
  unsuspendWireTransfer: async (userId) => {
    const response = await apiClient.put(`/admin/users/${userId}/unsuspend-wire-transfer`);
    return response.data;
  },
  getChatMessages: async () => {
    const response = await apiClient.get('/admin/chat/messages');
    return response.data;
  },
  replyChatMessage: async (userId, message) => {
    const response = await apiClient.post('/admin/chat/messages/reply', { userId, message });
    return response.data;
  }
};

// User API
export const userAPI = {
  register: async (userData) => {
    const response = await apiClient.post('/users/register', userData);
    return response.data;
  },
  login: async (accountNumber, password) => {
    const response = await apiClient.post('/users/login', { accountNumber, password });
    return response.data;
  },
  
  // OTP APIs
  generateOTP: async (email, purpose) => {
    const response = await apiClient.post('/users/otp/generate', { email, purpose });
    return response.data;
  },
  verifyOTP: async (email, otp_code, purpose) => {
    const response = await apiClient.post('/users/otp/verify', { email, otp_code, purpose });
    return response.data;
  },
  
  getProfile: async () => {
    const response = await apiClient.get('/users/profile');
    return response.data;
  },
  updateProfile: async (updateData) => {
    const response = await apiClient.put('/users/profile', updateData);
    return response.data;
  },
  uploadProfilePicture: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/users/profile-picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.put('/users/password', {
      currentPassword,
      newPassword
    });
    return response.data;
  },
  getBalance: async () => {
    const response = await apiClient.get('/users/balance');
    return response.data;
  },
  getTransactions: async () => {
    const response = await apiClient.get('/users/transactions');
    return response.data;
  },
  createTransfer: async (transferData) => {
    const response = await apiClient.post('/users/transfer', transferData);
    return response.data;
  },
  getNotifications: async () => {
    const response = await apiClient.get('/users/notifications');
    return response.data;
  },
  getMessages: async () => {
    const response = await apiClient.get('/users/messages');
    return response.data;
  },
  sendMessage: async (message) => {
    const response = await apiClient.post('/users/messages', { message });
    return response.data;
  },
  deleteNotification: async (notificationId) => {
    const response = await apiClient.delete(`/users/notifications/${notificationId}`);
    return response.data;
  },
  getChatMessages: async () => {
    const response = await apiClient.get('/users/chat/messages');
    return response.data;
  },
  sendChatMessage: async (message) => {
    const response = await apiClient.post('/users/chat/messages', { message });
    return response.data;
  },
  
  // Move Money APIs
  sendZellePayment: async (recipient, amount, message) => {
    const response = await apiClient.post('/users/zelle', { recipient, amount, message });
    return response.data;
  },
  scheduleBillPayment: async (payeeName, accountNumber, amount, paymentDate) => {
    const response = await apiClient.post('/users/billpay', { 
      payeeName, 
      accountNumber, 
      amount, 
      paymentDate 
    });
    return response.data;
  },
  depositCheck: async (checkNumber, amount, frontImage, backImage) => {
    const response = await apiClient.post('/users/remote-deposit', { 
      checkNumber, 
      amount, 
      frontImage, 
      backImage 
    });
    return response.data;
  },
  linkExternalAccount: async (bankName, accountType, routingNumber, accountNumber) => {
    const response = await apiClient.post('/users/external-account', { 
      bankName, 
      accountType, 
      routingNumber, 
      accountNumber 
    });
    return response.data;
  },
  getExternalAccounts: async () => {
    const response = await apiClient.get('/users/external-accounts');
    return response.data;
  },
  
  // Profile Picture APIs
  uploadProfilePicture: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/users/profile-picture`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.data;
  },
  deleteProfilePicture: async () => {
    const response = await apiClient.delete('/users/profile-picture');
    return response.data;
  },
  getProfilePictureUrl: (filename) => {
    return `${API_URL}/users/profile-picture/${filename}`;
  }
};

// Contact API (existing)
export const contactAPI = {
  submit: async (data) => {
    const response = await apiClient.post('/contact', data);
    return response.data;
  },
  getAll: async () => {
    const response = await apiClient.get('/contacts');
    return response.data;
  },
};

// Testimonials API (existing)
export const testimonialsAPI = {
  getAll: async () => {
    const response = await apiClient.get('/testimonials');
    return response.data;
  },
  create: async (data) => {
    const response = await apiClient.post('/testimonials', data);
    return response.data;
  },
};

// Initiatives API (existing)
export const initiativesAPI = {
  getAll: async (category = null) => {
    const params = category ? { category } : {};
    const response = await apiClient.get('/initiatives', { params });
    return response.data;
  },
  create: async (data) => {
    const response = await apiClient.post('/initiatives', data);
    return response.data;
  },
};

// Newsletter API (existing)
export const newsletterAPI = {
  subscribe: async (email) => {
    const response = await apiClient.post('/newsletter', { email });
    return response.data;
  },
  getAll: async () => {
    const response = await apiClient.get('/newsletters');
    return response.data;
  },
};

export default apiClient;
